import React, { useCallback, useEffect, useState } from "react";

import LoadingSpinner from "../LoadingSpinner";
import Modal from "../Modal";
import { Button } from "../ui/button";
import { useAuth } from "../../contexts/AuthContext";
import {
  COMMON_TIMEZONES,
  type Company,
  createCompany,
  fetchCompanies,
  updateCompany,
} from "../../services/adminApi";

type CompanyType = "customer" | "vendor";

const DOW_LABELS_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/**
 * Companies tab (super-admin, companies.manage). List companies, create one
 * (optionally bootstrapping its first admin user), and edit type / timezone.
 */
export const CompaniesTab: React.FC = () => {
  const { token } = useAuth();
  const [companies, setCompanies] = useState<Company[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Company | null>(null);
  const [creating, setCreating] = useState(false);

  const refresh = useCallback(() => {
    if (!token) return;
    fetchCompanies(token)
      .then(setCompanies)
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, [token]);

  useEffect(refresh, [refresh]);

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-semibold text-secondary-800">Companies</h2>
        <Button size="sm" onClick={() => setCreating(true)}>
          + New company
        </Button>
      </div>

      {error && (
        <div className="mb-3 text-sm text-error-700 bg-error-50 px-3 py-2 rounded">{error}</div>
      )}

      {!companies ? (
        <LoadingSpinner label="loading companies" />
      ) : (
        <div className="bg-white border border-secondary-200 rounded-md overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary-50 text-secondary-600 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-3 py-2">Company</th>
                <th className="text-left px-3 py-2">Type</th>
                <th className="text-left px-3 py-2">Timezone</th>
                <th className="text-left px-3 py-2">Cancellation</th>
                <th className="text-right px-3 py-2">Users</th>
                <th className="text-right px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {companies.map((c) => (
                <tr key={c.company_id} className="border-t border-secondary-100">
                  <td className="px-3 py-2 font-medium">{c.company_name ?? `#${c.company_id}`}</td>
                  <td className="px-3 py-2 capitalize">{c.company_type}</td>
                  <td className="px-3 py-2 text-secondary-600">{c.display_timezone ?? "—"}</td>
                  <td className="px-3 py-2 text-xs text-secondary-600">
                    {c.departure_cancellation_enabled ? (
                      <span title="Departure/Re-arrival pairs net out inside the configured window">
                        {DOW_LABELS_SHORT[c.departure_cancellation_cutoff_dow]}{" "}
                        {c.departure_cancellation_cutoff_hour}:00
                        {c.departure_cancellation_hide ? " · hide" : " · show"}
                      </span>
                    ) : (
                      <span className="text-secondary-400">off</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right text-secondary-600">{c.user_count}</td>
                  <td className="px-3 py-2 text-right">
                    <button
                      type="button"
                      className="text-xs text-primary-600 hover:underline"
                      onClick={() => setEditing(c)}
                    >
                      edit
                    </button>
                  </td>
                </tr>
              ))}
              {companies.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-3 py-6 text-center text-secondary-500">
                    No companies.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {creating && (
        <CompanyCreateModal
          token={token ?? ""}
          onClose={() => setCreating(false)}
          onDone={() => {
            setCreating(false);
            refresh();
          }}
        />
      )}
      {editing && (
        <CompanyEditModal
          token={token ?? ""}
          company={editing}
          onClose={() => setEditing(null)}
          onDone={() => {
            setEditing(null);
            refresh();
          }}
        />
      )}
    </div>
  );
};

const TzSelect: React.FC<{ value: string; onChange: (v: string) => void }> = ({
  value,
  onChange,
}) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className="w-full border border-secondary-300 rounded px-2 py-1.5 text-sm bg-white"
  >
    <option value="">(none)</option>
    {COMMON_TIMEZONES.map((tz) => (
      <option key={tz} value={tz}>
        {tz}
      </option>
    ))}
  </select>
);

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <label className="block">
    <span className="block text-xs uppercase tracking-wide text-secondary-500 mb-1">{label}</span>
    {children}
  </label>
);

const inputCls = "w-full border border-secondary-300 rounded px-2 py-1.5 text-sm";

const CompanyCreateModal: React.FC<{
  token: string;
  onClose: () => void;
  onDone: () => void;
}> = ({ token, onClose, onDone }) => {
  const [name, setName] = useState("");
  const [ctype, setCtype] = useState<CompanyType>("customer");
  const [tz, setTz] = useState("America/Chicago");
  const [withAdmin, setWithAdmin] = useState(true);
  const [adminFirst, setAdminFirst] = useState("");
  const [adminEmail, setAdminEmail] = useState("");
  const [adminPw, setAdminPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = async (): Promise<void> => {
    if (!name.trim()) return setErr("Company name is required.");
    if (withAdmin && (!adminEmail.includes("@") || adminPw.length < 8)) {
      return setErr("Admin needs a valid email and an 8+ character password.");
    }
    setBusy(true);
    setErr(null);
    try {
      await createCompany(
        {
          company_name: name.trim(),
          company_type: ctype,
          display_timezone: tz || null,
          admin: withAdmin
            ? { first_name: adminFirst || undefined, email: adminEmail.trim(), password: adminPw }
            : undefined,
        },
        token,
      );
      onDone();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open onClose={onClose} title="New company" widthClass="max-w-lg">
      <div className="space-y-3">
        {err && <div className="text-sm text-error-700 bg-error-50 px-3 py-2 rounded">{err}</div>}
        <Field label="Company name">
          <input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Type">
            <select
              className={inputCls + " bg-white"}
              value={ctype}
              onChange={(e) => setCtype(e.target.value as CompanyType)}
            >
              <option value="customer">Customer</option>
              <option value="vendor">Vendor</option>
            </select>
          </Field>
          <Field label="Display timezone">
            <TzSelect value={tz} onChange={setTz} />
          </Field>
        </div>

        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={withAdmin} onChange={(e) => setWithAdmin(e.target.checked)} />
          Create an initial admin user
        </label>
        {withAdmin && (
          <div className="grid grid-cols-1 gap-3 border border-secondary-200 rounded p-3">
            <Field label="Admin name">
              <input className={inputCls} value={adminFirst} onChange={(e) => setAdminFirst(e.target.value)} />
            </Field>
            <Field label="Admin email">
              <input className={inputCls} value={adminEmail} onChange={(e) => setAdminEmail(e.target.value)} />
            </Field>
            <Field label="Initial password (8+ chars)">
              <input
                type="password"
                className={inputCls}
                value={adminPw}
                onChange={(e) => setAdminPw(e.target.value)}
              />
            </Field>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="outline" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" disabled={busy} onClick={() => void save()}>
            {busy ? "Creating…" : "Create"}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

const DOW_LABELS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

const CompanyEditModal: React.FC<{
  token: string;
  company: Company;
  onClose: () => void;
  onDone: () => void;
}> = ({ token, company, onClose, onDone }) => {
  const [name, setName] = useState(company.company_name ?? "");
  const [ctype, setCtype] = useState<CompanyType>(
    company.company_type === "vendor" ? "vendor" : "customer",
  );
  const [tz, setTz] = useState(company.display_timezone ?? "");
  // Departure / Re-arrival cancellation knobs (migrations 078, 079).
  const [cancelEnabled, setCancelEnabled] = useState<boolean>(
    Boolean(company.departure_cancellation_enabled),
  );
  const [cancelDow, setCancelDow] = useState<number>(
    Number(company.departure_cancellation_cutoff_dow ?? 0),
  );
  const [cancelHour, setCancelHour] = useState<number>(
    Number(company.departure_cancellation_cutoff_hour ?? 0),
  );
  const [cancelHide, setCancelHide] = useState<boolean>(
    Boolean(company.departure_cancellation_hide),
  );
  // Consignment-report knobs (migrations 088 / 090 / 092).
  const [displayAbbr, setDisplayAbbr] = useState<string>(
    company.display_abbr ?? "",
  );
  const [conDow, setConDow] = useState<number>(
    Number(company.consignment_cutoff_dow ?? 0),
  );
  const [conHour, setConHour] = useState<number>(
    Number(company.consignment_cutoff_hour ?? 0),
  );
  const [conType, setConType] = useState<string>(
    (company.consignment_default_type ?? "C").slice(0, 1).toUpperCase() || "C",
  );
  const [conReqDefault, setConReqDefault] = useState<string>(
    company.consignment_requester_default ?? "",
  );
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = async (): Promise<void> => {
    if (!name.trim()) return setErr("Company name is required.");
    setBusy(true);
    setErr(null);
    try {
      await updateCompany(
        company.company_id,
        {
          company_name: name.trim(),
          company_type: ctype,
          display_timezone: tz || null,
          departure_cancellation_enabled: cancelEnabled,
          departure_cancellation_cutoff_dow: cancelDow,
          departure_cancellation_cutoff_hour: cancelHour,
          departure_cancellation_hide: cancelHide,
          display_abbr: displayAbbr.trim() || null,
          consignment_cutoff_dow: conDow,
          consignment_cutoff_hour: conHour,
          consignment_default_type: conType,
          consignment_requester_default: conReqDefault.trim() || null,
        },
        token,
      );
      onDone();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  // Render the cutoff hour as "5 PM" (operators describe their week
  // boundary in 12-hour clock, even though we store 0..23).
  const hourLabel = (h: number): string => {
    if (h === 0) return "12 AM (midnight)";
    if (h === 12) return "12 PM (noon)";
    return h < 12 ? `${h} AM` : `${h - 12} PM`;
  };

  return (
    <Modal open onClose={onClose} title={`Edit ${company.company_name ?? "company"}`} widthClass="max-w-lg">
      <div className="space-y-3">
        {err && <div className="text-sm text-error-700 bg-error-50 px-3 py-2 rounded">{err}</div>}
        <Field label="Company name">
          <input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Type">
            <select
              className={inputCls + " bg-white"}
              value={ctype}
              onChange={(e) => setCtype(e.target.value as CompanyType)}
            >
              <option value="customer">Customer</option>
              <option value="vendor">Vendor</option>
            </select>
          </Field>
          <Field label="Display timezone">
            <TzSelect value={tz} onChange={setTz} />
          </Field>
        </div>

        <fieldset className="border border-secondary-200 rounded p-3">
          <legend className="px-1 text-xs uppercase tracking-wide text-secondary-500">
            Departure / Re-arrival cancellation
          </legend>
          <p className="text-xs text-secondary-600 mb-2">
            When a tag's CONFIRMED Departure is followed by a Re-arrival
            (false-positive take) <em>in the same weekly window</em>, both
            rows are marked as cancelled and net out. Window resets at
            the chosen weekday + hour in this company's display timezone.
          </p>
          <label className="flex items-center gap-2 text-sm mb-2">
            <input
              type="checkbox"
              checked={cancelEnabled}
              onChange={(e) => setCancelEnabled(e.target.checked)}
            />
            Enable Departure / Re-arrival cancellation for this company
          </label>
          {cancelEnabled && (
            <>
              <div className="grid grid-cols-2 gap-3 mb-2">
                <Field label="Cutoff weekday">
                  <select
                    className={inputCls + " bg-white"}
                    value={cancelDow}
                    onChange={(e) => setCancelDow(Number(e.target.value))}
                  >
                    {DOW_LABELS.map((d, i) => (
                      <option key={d} value={i}>
                        {d}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Cutoff hour (local)">
                  <select
                    className={inputCls + " bg-white"}
                    value={cancelHour}
                    onChange={(e) => setCancelHour(Number(e.target.value))}
                  >
                    {Array.from({ length: 24 }, (_, h) => (
                      <option key={h} value={h}>
                        {hourLabel(h)}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={cancelHide}
                  onChange={(e) => setCancelHide(e.target.checked)}
                />
                Hide cancelled rows for this company's users
                <span className="text-xs text-secondary-500 ml-1">
                  (platform users always see them with strikethrough)
                </span>
              </label>
            </>
          )}
        </fieldset>

        {ctype === "vendor" && (
          <fieldset className="border border-secondary-200 rounded p-3">
            <legend className="px-1 text-xs uppercase tracking-wide text-secondary-500">
              Consignment invoicing (vendor)
            </legend>
            <p className="text-xs text-secondary-600 mb-2">
              Short name emitted on every line of the weekly consignment
              report (<code>companyNameAbbr</code>) and uppercased into the
              file's <code>headerDescription</code>. Leave blank to fall
              back to the legal company name.
            </p>
            <Field label="Display abbreviation (e.g. “Premium”)">
              <input
                className={inputCls}
                value={displayAbbr}
                maxLength={40}
                onChange={(e) => setDisplayAbbr(e.target.value)}
              />
            </Field>
          </fieldset>
        )}

        {ctype === "customer" && (
          <fieldset className="border border-secondary-200 rounded p-3">
            <legend className="px-1 text-xs uppercase tracking-wide text-secondary-500">
              Consignment invoicing (customer)
            </legend>
            <p className="text-xs text-secondary-600 mb-2">
              Week boundary for the weekly consignment report. Each invoice
              window is <em>[cutoff, cutoff + 7 days)</em> in this
              company's display timezone.
            </p>
            <div className="grid grid-cols-2 gap-3 mb-2">
              <Field label="Cutoff weekday">
                <select
                  className={inputCls + " bg-white"}
                  value={conDow}
                  onChange={(e) => setConDow(Number(e.target.value))}
                >
                  {DOW_LABELS.map((d, i) => (
                    <option key={d} value={i}>
                      {d}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Cutoff hour (local)">
                <select
                  className={inputCls + " bg-white"}
                  value={conHour}
                  onChange={(e) => setConHour(Number(e.target.value))}
                >
                  {Array.from({ length: 24 }, (_, h) => (
                    <option key={h} value={h}>
                      {hourLabel(h)}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Default line type (single char)">
                <input
                  className={inputCls + " font-mono"}
                  value={conType}
                  maxLength={1}
                  onChange={(e) =>
                    setConType((e.target.value || "C").slice(0, 1).toUpperCase())
                  }
                />
              </Field>
              <Field label="Default requester id (fallback)">
                <input
                  className={inputCls + " font-mono"}
                  value={conReqDefault}
                  maxLength={64}
                  placeholder="e.g. 100209405"
                  onChange={(e) => setConReqDefault(e.target.value)}
                />
              </Field>
            </div>
          </fieldset>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="outline" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" disabled={busy} onClick={() => void save()}>
            {busy ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default CompaniesTab;
